import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { callClaude } from '../claude'

function TcVarianceAnalysis({ opportunity, profile, onClose }) {
  const [framework, setFramework] = useState(null)
  const [analysis, setAnalysis] = useState(null)
  const [analysing, setAnalysing] = useState(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    if (opportunity.framework_id) {
      const { data } = await supabase
        .from('frameworks')
        .select('id, name, reference_code, tc_url')
        .eq('id', opportunity.framework_id)
        .single()
      setFramework(data)
    }
    if (opportunity.tc_variance_analysis) {
      setAnalysis(opportunity.tc_variance_analysis)
    }
    setLoading(false)
  }

  const handleAnalyse = async () => {
    if (!framework?.tc_url || !opportunity.tc_url) return
    setAnalysing(true)
    setError(null)

    try {
      const system = `You are an expert commercial lawyer and bid manager specialising in Government and Defence procurement. Analyse two sets of terms and conditions and identify material variances. Be precise, commercially focused, and flag genuine risks. Respond in JSON only.`

      const prompt = `Analyse the variance between these two sets of terms and conditions.

Opportunity: ${opportunity.title}
Buyer: ${opportunity.buyer}
Framework: ${framework.name} (${framework.reference_code || ''})

Framework Standard T&C URL: ${framework.tc_url}
Buyer Specific T&C URL: ${opportunity.tc_url}

Note: As you cannot directly access URLs, base your analysis on the information provided and flag that the buyer should verify specific clauses against the actual documents.

Identify the most common areas where buyers deviate from standard framework terms and provide a structured risk assessment.

Respond with a JSON object:
{
  "summary": "2-3 sentence overview of the variance analysis",
  "risk_level": "High" | "Medium" | "Low",
  "variances": [
    {
      "area": "area name e.g. Payment Terms, Liability, IPR",
      "risk": "High" | "Medium" | "Low",
      "description": "What typically deviates and why it matters commercially",
      "recommended_action": "What the bid team should do"
    }
  ],
  "disclaimer": "Short note that this is AI-generated and documents should be reviewed by legal counsel"
}`

      const result = await callClaude({ system, prompt, max_tokens: 1500 })
      const clean = result.replace(/```json/g, '').replace(/```/g, '').trim()
      const jsonMatch = clean.match(/\{[\s\S]*\}/)
      if (!jsonMatch) throw new Error('No JSON in response')
      const parsed = JSON.parse(jsonMatch[0])

      await supabase
        .from('opportunities')
        .update({ tc_variance_analysis: parsed })
        .eq('id', opportunity.id)

      setAnalysis(parsed)
    } catch (err) {
      console.error('TC analysis error:', err)
      setError('Analysis failed. Please try again.')
    }
    setAnalysing(false)
  }

  const riskColour = (risk) => {
    if (risk === 'High') return 'var(--red)'
    if (risk === 'Medium') return '#E67E22'
    return 'var(--green)'
  }

  const canAnalyse = framework?.tc_url && opportunity.tc_url && profile?.organisations?.ai_enabled

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      <div style={{ background: 'var(--green-dark)', padding: '20px 24px', marginBottom: '24px' }}>
        <div style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '6px' }}>T&C Variance Analysis</div>
        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 600, color: 'var(--white)' }}>{opportunity.title}</div>
        <div style={{ fontSize: '0.875rem', color: 'var(--silver)', marginTop: '4px' }}>{opportunity.buyer}</div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {loading && <p style={{ color: 'var(--slate)' }}>Loading...</p>}

      {!loading && (
        <div>
          {/* URL status */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card__header"><span className="card__title">Document Sources</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '6px' }}>
                  Framework T&C — {framework?.name || 'No framework linked'}
                </div>
                {framework?.tc_url ? (
                  <a href={framework.tc_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8125rem', color: 'var(--green)', wordBreak: 'break-all' }}>{framework.tc_url}</a>
                ) : (
                  <p style={{ fontSize: '0.8125rem', color: 'var(--red)', margin: 0 }}>No T&C URL on framework record. Add one in Framework Library.</p>
                )}
              </div>
              <div>
                <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '6px' }}>Buyer Specific T&C</div>
                {opportunity.tc_url ? (
                  <a href={opportunity.tc_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8125rem', color: 'var(--green)', wordBreak: 'break-all' }}>{opportunity.tc_url}</a>
                ) : (
                  <p style={{ fontSize: '0.8125rem', color: 'var(--red)', margin: 0 }}>No T&C URL on opportunity. Add one by editing the opportunity.</p>
                )}
              </div>
            </div>
            {canAnalyse && !analysis && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--green-light)' }}>
                <button
                  className="btn btn--primary"
                  onClick={handleAnalyse}
                  disabled={analysing}
                >
                  {analysing ? '⏳ Analysing T&C documents...' : '✦ Analyse T&C Variances'}
                </button>
              </div>
            )}
            {!profile?.organisations?.ai_enabled && (
              <p style={{ fontSize: '0.8125rem', color: 'var(--slate)', marginTop: '12px', fontStyle: 'italic' }}>AI features are disabled for your organisation. Enable in Settings → Organisation Settings.</p>
            )}
          </div>

          {/* Analysis results */}
          {analysis && (
            <div>
              <div style={{ background: 'var(--green-dark)', padding: '16px 20px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--white)', flex: 1, lineHeight: 1.5 }}>{analysis.summary}</div>
                <div style={{ flexShrink: 0, marginLeft: '20px' }}>
                  <span style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 12px', background: riskColour(analysis.risk_level), color: 'var(--white)' }}>
                    {analysis.risk_level} Risk
                  </span>
                </div>
              </div>

              {analysis.variances && analysis.variances.map((v, i) => (
                <div key={i} style={{ background: 'var(--white)', border: '1px solid var(--green-light)', borderLeft: '4px solid ' + riskColour(v.risk), marginBottom: '10px', padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.125rem', fontWeight: 600, color: 'var(--green-dark)' }}>{v.area}</div>
                    <span style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '2px 10px', background: riskColour(v.risk), color: 'var(--white)' }}>{v.risk} Risk</span>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--charcoal)', margin: '0 0 8px', lineHeight: 1.5 }}>{v.description}</p>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--green-dark)', fontWeight: 600, background: 'var(--green-pale)', padding: '8px 12px' }}>
                    ➜ {v.recommended_action}
                  </div>
                </div>
              ))}

              {analysis.disclaimer && (
                <p style={{ fontSize: '0.75rem', color: 'var(--slate)', fontStyle: 'italic', marginTop: '16px', padding: '12px', background: 'var(--off-white)', border: '1px solid var(--silver)' }}>{analysis.disclaimer}</p>
              )}

              <div style={{ marginTop: '16px' }}>
                <button
                  onClick={handleAnalyse}
                  disabled={analysing}
                  style={{ background: 'none', border: '1px solid var(--green)', color: 'var(--green)', padding: '6px 16px', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  {analysing ? 'Re-analysing...' : '↻ Re-run Analysis'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="actions" style={{ marginTop: '24px' }}>
        <button className="btn btn--secondary" onClick={onClose}>← Back to Pipeline</button>
      </div>
    </div>
  )
}

export default TcVarianceAnalysis